import { useCallback, useEffect, useRef, useState } from 'react';
import type { RuleViolation } from '@td/shared';
import { Modal } from './Modal';

const SOUND_KEY = 'td.ruleSound';

/**
 * A short two-tone chime, synthesised rather than shipped.
 *
 * An audio file would be another asset to load and cache for something that
 * plays for a fifth of a second; two oscillators cost nothing and can't 404.
 * Deliberately a soft interval rather than an alarm — this is a nudge in a
 * classroom, not a fire panel.
 */
function playChime() {
  try {
    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;

    const ctx = new Ctor();
    const now = ctx.currentTime;

    [880, 660].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;

      const at = now + i * 0.14;
      gain.gain.setValueAtTime(0, at);
      gain.gain.linearRampToValueAtTime(0.18, at + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.13);

      osc.connect(gain).connect(ctx.destination);
      osc.start(at);
      osc.stop(at + 0.15);
    });

    // Free the hardware once the sound has finished.
    window.setTimeout(() => void ctx.close(), 600);
  } catch {
    // Audio blocked until the page has been interacted with, or unavailable.
    // The banner and the dialog carry the message on their own.
  }
}

export function useRuleSound() {
  const [enabled, setEnabled] = useState(() => {
    try {
      return localStorage.getItem(SOUND_KEY) !== 'off';
    } catch {
      return true;
    }
  });

  const toggle = useCallback(() => {
    setEnabled((on) => {
      const next = !on;
      try {
        localStorage.setItem(SOUND_KEY, next ? 'on' : 'off');
      } catch {
        // Not remembered, still applies for this session.
      }
      if (next) playChime();
      return next;
    });
  }, []);

  const play = useCallback(() => {
    if (enabled) playChime();
  }, [enabled]);

  return { enabled, toggle, play };
}

/**
 * The dialog that appears the moment a seating change breaks a rule.
 *
 * It offers to undo, because the usual response to "you just sat those two
 * together" is to put it back. Keeping the arrangement stays available: the
 * teacher may be doing it on purpose, and the app does not get a vote.
 */
export function RuleAlert({
  violations,
  onUndo,
  onDismiss,
}: {
  violations: RuleViolation[];
  onUndo: (() => void) | null;
  onDismiss: () => void;
}) {
  const dismissRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    dismissRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onDismiss();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onDismiss]);

  return (
    <Modal
      onClose={onDismiss}
      label="Seating rule broken"
      backdrop="bg-espresso/40 print:hidden"
      zIndex={70}
      className="w-full max-w-md overflow-hidden rounded-card border-2 border-rust bg-white shadow-lifted"
    >
      <div role="alertdialog">
        <div className="flex items-center gap-3 bg-rose/25 px-5 py-3">
          <span
            aria-hidden
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-rust text-lg text-cream"
          >
            !
          </span>
          <h2 className="text-base text-espresso">
            {violations.length === 1
              ? 'That breaks a seating rule'
              : `That breaks ${violations.length} seating rules`}
          </h2>
        </div>

        <ul className="space-y-1.5 px-5 py-4">
          {violations.map((v) => (
            <li key={v.ruleId} className="text-sm leading-snug text-espresso">
              {v.message}
            </li>
          ))}
        </ul>

        <div className="flex justify-end gap-2 border-t border-sand px-5 py-3">
          <button ref={dismissRef} onClick={onDismiss} className="btn-secondary">
            Keep it anyway
          </button>
          {onUndo && (
            <button
              onClick={() => {
                onUndo();
                onDismiss();
              }}
              className="btn-primary"
            >
              Undo the move
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}

/** The always-on banner above the room whenever anything is out of place. */
export function RuleBanner({
  violations,
  onHighlight,
  soundOn,
  onToggleSound,
}: {
  violations: RuleViolation[];
  onHighlight: (itemIds: string[] | null) => void;
  soundOn: boolean;
  onToggleSound: () => void;
}) {
  if (violations.length === 0) return null;

  return (
    <div className="mb-3 overflow-hidden rounded-card border-2 border-rust bg-rose/20 print:hidden">
      <div className="flex items-center gap-2 px-4 py-2">
        <span
          aria-hidden
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-rust text-sm text-cream"
        >
          !
        </span>
        <p className="flex-1 text-sm text-espresso">
          {violations.length === 1
            ? '1 seating rule is being broken'
            : `${violations.length} seating rules are being broken`}
        </p>
        <button
          onClick={onToggleSound}
          aria-pressed={soundOn}
          title={soundOn ? 'Sound on when a rule breaks' : 'Sound off'}
          className="btn-soft border-rust/30 bg-white/60"
        >
          {soundOn ? '♪ Sound on' : '♪ Sound off'}
        </button>
      </div>

      <ul className="border-t border-rust/30 px-4 py-2">
        {violations.map((v) => (
          <li
            key={v.ruleId}
            onMouseEnter={() => onHighlight(v.itemIds)}
            onMouseLeave={() => onHighlight(null)}
            className="cursor-default py-0.5 text-sm leading-snug text-rust"
          >
            {v.message}
          </li>
        ))}
      </ul>
    </div>
  );
}
